import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { useCallback } from 'react';
import { useStore } from 'zustand';

import { amplitudeTrack } from 'app/utils/track/amplitude';

import {
  INVENTORY_DATE_SIZE,
  inventoryStore,
} from '../../../../InventoryStore';
import type { InventoryTabItemName } from '../../../../InventoryStore';
import {
  findInventoryDifferences,
  mergeInventoryObjects,
} from '../../../../utils';

dayjs.extend(weekOfYear);

export const useTimePeriodNavigation = (tab: InventoryTabItemName) => {
  const modifiedInventory = useStore(
    inventoryStore,
    (state) => state.modifiedInventory
  );

  const inventory = useStore(inventoryStore, (state) => state.inventory);
  const timePeriodStore = useStore(
    inventoryStore,
    (state) => state.timePeriodStore
  );
  const setTimePeriodStore = useStore(
    inventoryStore,
    (state) => state.setTimePeriodStore
  );

  const changeTimePeriod = useStore(
    inventoryStore,
    (state) => state.changeTimePeriod
  );

  const navigateToPeriod = useCallback(
    (newStartDate: Dayjs) => {
      const newDiffs = findInventoryDifferences(
        inventory ?? {},
        modifiedInventory ?? {}
      );

      const mergedStore = mergeInventoryObjects(timePeriodStore, newDiffs);

      setTimePeriodStore(mergedStore);

      changeTimePeriod(newStartDate);
    },
    [
      changeTimePeriod,
      inventory,
      modifiedInventory,
      setTimePeriodStore,
      timePeriodStore,
    ]
  );

  const handlePrevious = useCallback(
    (startDate: Dayjs) => {
      const newStartDate = startDate.subtract(INVENTORY_DATE_SIZE, 'day');

      amplitudeTrack('Click previous sales arrow', {
        week_number: newStartDate.week(),
      });

      navigateToPeriod(newStartDate);
    },
    [navigateToPeriod]
  );

  const handleNext = useCallback(
    (startDate: Dayjs) => {
      const newStartDate = startDate.add(INVENTORY_DATE_SIZE, 'day');

      amplitudeTrack('Click next sales arrow', {
        week_number: newStartDate.week(),
      });

      navigateToPeriod(newStartDate);
    },
    [navigateToPeriod]
  );

  const handleToday = useCallback(() => {
    const today = dayjs();

    amplitudeTrack('Click today button', {
      tab,
    });

    navigateToPeriod(today.startOf('day'));
  }, [navigateToPeriod, tab]);

  return {
    handlePrevious,
    handleNext,
    handleToday,
  };
};
